<template>
    <tailwind-layout title="Rent Autopay">
        <div class="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

            <!-- Back button -->
            <Link :href="route('my-property')"
                  class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to my property
            </Link>

            <!-- Page header -->
            <div class="mb-6">
                <h1 class="text-2xl font-bold text-gray-900">Rent Autopay</h1>
                <p class="text-sm text-gray-600 mt-2">
                    Set up automatic monthly rent payments via your linked bank account.
                </p>
            </div>

            <!-- Active schedule view -->
            <div v-if="schedule && schedule.status === 'active'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div class="flex items-start justify-between mb-4">
                    <div v-if="schedule.action_needed">
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Action needed
                        </span>
                        <h2 class="text-lg font-semibold text-gray-900 mt-2">Autopay couldn't take this month's rent</h2>
                        <p v-if="schedule.rent_payments_stopped" class="text-sm text-gray-700 mt-1">
                            Your landlord has turned off rent payments for this property, so the payment due {{ schedule.next_payment_date }} can't be taken or paid here right now. Autopay checks again each day until {{ schedule.retry_until }} and goes through if your landlord turns rent payments back on. Please contact your landlord about this month's rent.
                        </p>
                        <p v-else class="text-sm text-gray-700 mt-1">
                            The payment due {{ schedule.next_payment_date }} couldn't be made yet. Autopay tries it again each day until {{ schedule.retry_until }} and goes through as soon as the problem is fixed. If your bank account was disconnected, reconnecting it is all you need to do. You can also pay it manually; autopay never takes a month that's already paid.
                        </p>
                    </div>
                    <div v-else>
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                        </span>
                        <h2 class="text-lg font-semibold text-gray-900 mt-2">Autopay is on</h2>
                    </div>
                </div>
                <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                        <dt class="text-gray-500">Amount</dt>
                        <dd class="text-gray-900 font-medium">${{ schedule.amount_dollars }} / month</dd>
                    </div>
                    <div>
                        <dt class="text-gray-500">Day of month</dt>
                        <dd class="text-gray-900 font-medium">{{ ordinal(schedule.day_of_month) }}</dd>
                    </div>
                    <div v-if="schedule.next_payment_date && !schedule.action_needed">
                        <dt class="text-gray-500">Next payment</dt>
                        <dd class="text-gray-900 font-medium">{{ schedule.next_payment_date }}</dd>
                    </div>
                    <div v-if="schedule.last_payment_date">
                        <dt class="text-gray-500">Last payment</dt>
                        <dd class="text-gray-900 font-medium">{{ schedule.last_payment_date }}</dd>
                    </div>
                </dl>

                <div class="mt-6 pt-6 border-t border-gray-200">
                    <button
                        type="button"
                        @click="confirmCancel"
                        class="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                        :disabled="cancelling"
                    >
                        {{ cancelling ? 'Cancelling…' : 'Cancel Autopay' }}
                    </button>
                </div>
            </div>

            <!-- No active schedule, and this lease can't take autopay (same
                 checks the server applies on submit) -->
            <div v-else-if="unavailable_reason" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-2">Autopay isn't available</h2>
                <p class="text-sm text-gray-700">{{ unavailable_reason }}</p>
            </div>

            <!-- IDV gate (no active schedule, KYC not completed) -->
            <div v-else-if="!is_kyc" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-2">Verify your identity</h2>
                <p class="text-sm text-gray-700 mb-4">
                    Before setting up autopay, we need to verify your identity. This is a one-time step required by our payment processor and ACH compliance rules. Once you've verified, you'll be able to add a bank account and enable autopay.
                </p>
                <button
                    type="button"
                    @click="startIdv"
                    :disabled="!idv_link_token || verifying"
                    class="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rent-risk-blue-400 hover:bg-black disabled:opacity-50">
                    {{ verifying ? 'Opening verification…' : 'Verify my identity' }}
                </button>
                <p v-if="!idv_link_token" class="mt-2 text-xs text-red-600">
                    We couldn't load the verification step. Please refresh the page or contact support.
                </p>
            </div>

            <!-- Setup form (no active schedule, KYC complete) -->
            <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-4">Set up Autopay</h2>

                <form @submit.prevent="submit" class="space-y-5">

                    <!-- Bank account selector. If user has linked accounts, show
                         a dropdown. Otherwise, "Connect a bank account" launches
                         Plaid Link via the existing exchange-token endpoint. -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Bank account</label>
                        <div v-if="bank_accounts && bank_accounts.length > 0" class="space-y-2">
                            <select v-model.number="form.user_bank_account_id"
                                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400"
                                    required>
                                <option value="" disabled>Choose a connected bank account…</option>
                                <option v-for="acct in bank_accounts" :key="acct.id" :value="acct.id">
                                    {{ acct.display }}
                                </option>
                            </select>
                            <button type="button"
                                    @click="connectBank"
                                    :disabled="connectingBank"
                                    class="text-xs text-rent-risk-blue-600 hover:underline disabled:opacity-50">
                                {{ connectingBank ? 'Opening Plaid…' : '+ Add another bank account' }}
                            </button>
                        </div>
                        <button v-else
                                type="button"
                                @click="connectBank"
                                :disabled="connectingBank"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                            {{ connectingBank ? 'Opening Plaid…' : 'Connect a bank account' }}
                        </button>
                        <p v-if="form.errors.user_bank_account_id" class="mt-1 text-sm text-red-600">
                            {{ form.errors.user_bank_account_id }}
                        </p>
                    </div>

                    <!-- Day of month -->
                    <div>
                        <label for="day_of_month" class="block text-sm font-medium text-gray-700 mb-1">
                            Day of the month to pay
                        </label>
                        <select id="day_of_month"
                                v-model.number="form.day_of_month"
                                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400">
                            <option v-for="day in 28" :key="day" :value="day">{{ ordinal(day) }}</option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500">
                            We only support days 1–28 to avoid issues in February and other short months.
                        </p>
                        <p v-if="form.errors.day_of_month" class="mt-1 text-sm text-red-600">
                            {{ form.errors.day_of_month }}
                        </p>
                    </div>

                    <!-- Amount (locked to lease rent — autopay debits the
                         exact rent amount. Tenants who need a different
                         amount can pay manually outside autopay.) -->
                    <div>
                        <label for="amount_dollars" class="block text-sm font-medium text-gray-700 mb-1">
                            Monthly amount
                        </label>
                        <div class="relative">
                            <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">$</span>
                            <input id="amount_dollars"
                                   :value="amountDollars"
                                   type="number"
                                   readonly
                                   class="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-gray-50 text-gray-700 cursor-not-allowed">
                        </div>
                        <p class="mt-1 text-xs text-gray-500">
                            Locked to your lease rent. Autopay will debit this amount each month.
                        </p>
                    </div>

                    <!-- Start date -->
                    <div>
                        <label for="start_date" class="block text-sm font-medium text-gray-700 mb-1">
                            First payment date
                        </label>
                        <input id="start_date"
                               v-model="form.start_date"
                               type="date"
                               :min="todayIsoDate"
                               class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400"
                               required>
                        <p v-if="form.errors.start_date" class="mt-1 text-sm text-red-600">
                            {{ form.errors.start_date }}
                        </p>
                    </div>

                    <!-- ACH authorization -->
                    <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <h3 class="text-sm font-semibold text-gray-900 mb-2">ACH Authorization</h3>
                        <p class="text-xs text-gray-700 whitespace-pre-line">{{ ach_authorization_text }}</p>
                    </div>

                    <!-- Signature pad -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Sign to authorize</label>
                        <signature-pad ref="signaturePad"
                                       label="Signature"
                                       :required="true"
                                       :error="form.errors.authorization_signature_base64" />
                    </div>

                    <!-- Acknowledgment -->
                    <div class="flex items-start">
                        <input id="ack" type="checkbox" v-model="form.authorization_acknowledged"
                               class="mt-1 h-4 w-4 text-rent-risk-blue-400 border-gray-300 rounded focus:ring-rent-risk-blue-400">
                        <label for="ack" class="ml-2 text-sm text-gray-700">
                            I have read and agree to the ACH authorization above.
                        </label>
                    </div>
                    <p v-if="form.errors.authorization_acknowledged" class="text-sm text-red-600">
                        {{ form.errors.authorization_acknowledged }}
                    </p>

                    <!-- Submit -->
                    <div class="pt-4 border-t border-gray-200 flex justify-end">
                        <button type="submit"
                                :disabled="form.processing || !form.user_bank_account_id"
                                class="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rent-risk-blue-400 hover:bg-black disabled:opacity-50">
                            {{ form.processing ? 'Setting up…' : 'Set up Autopay' }}
                        </button>
                    </div>
                </form>
            </div>

            <!-- Cancelled history note -->
            <div v-if="schedule && schedule.status === 'cancelled'" class="text-sm text-gray-500 text-center mt-6">
                Your previous autopay was cancelled. You can set up a new one above at any time.
            </div>

            <!-- Inline error banner for client-side errors (Plaid Link
                 setup failures). Replaces alert() per Ihtisham's PR review. -->
            <div v-if="errorMessage"
                 class="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start justify-between">
                <span>{{ errorMessage }}</span>
                <button type="button" class="ml-3 text-red-400 hover:text-red-600" @click="errorMessage = ''">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Cancel-autopay confirmation modal. Replaces window.confirm()
             per Ihtisham's PR review (RentRisk uses in-app modals, not
             native browser dialogs). -->
        <jetstreem-modal :show="showCancelModal" :max-width="'md'" @close="showCancelModal = false">
            <div class="px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-900">Cancel autopay?</h2>
                <p class="mt-2 text-sm text-gray-700">
                    You will need to make manual rent payments going forward. Your bank account stays linked — you can re-enable autopay anytime.
                </p>
            </div>
            <div class="px-6 py-3 bg-gray-50 flex justify-end space-x-2">
                <button type="button"
                        @click="showCancelModal = false"
                        class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-100">
                    Keep autopay
                </button>
                <button type="button"
                        @click="confirmCancelInModal"
                        :disabled="cancelling"
                        class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                    {{ cancelling ? 'Cancelling…' : 'Yes, cancel autopay' }}
                </button>
            </div>
        </jetstreem-modal>
    </tailwind-layout>
</template>

<script>
import axios from 'axios';
import { useForm, router, Link } from '@inertiajs/vue3';
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import SignaturePad from '@/Components/SignaturePad.vue';
import JetstreemModal from '@/Jetstream/Modal.vue';

export default {
    components: { TailwindLayout, SignaturePad, Link, JetstreemModal },
    props: {
        rentedProperty: { type: Object, required: true },
        schedule: { type: Object, default: null },
        unavailable_reason: { type: String, default: null },
        bank_accounts: { type: Array, default: () => [] },
        ach_authorization_text: { type: String, required: true },
        is_kyc: { type: Boolean, default: false },
        idv_link_token: { type: String, default: null },
    },
    data() {
        return {
            cancelling: false,
            connectingBank: false,
            verifying: false,
            showCancelModal: false,
            errorMessage: '',
            amountDollars: this.rentedProperty.rent ? Number(this.rentedProperty.rent).toFixed(2) : '',
            form: useForm({
                user_bank_account_id: this.bank_accounts.length === 1 ? this.bank_accounts[0].id : '',
                day_of_month: 5,
                amount_cents: 0,
                start_date: '',
                authorization_signature_base64: '',
                authorization_acknowledged: false,
            }),
        };
    },
    computed: {
        todayIsoDate() {
            return new Date().toISOString().slice(0, 10);
        },
    },
    methods: {
        ordinal(n) {
            const s = ['th', 'st', 'nd', 'rd'];
            const v = n % 100;
            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        },

        // RAC-9993: launch Plaid Link in IDV mode. Mirrors the existing
        // pay flow at Pages/RentedProperties/Pay.vue:416 — onSuccess
        // posts the IDV result to plaid.idv.submit (which creates the
        // UserKyc row synchronously). We can't rely on the IDV webhook
        // alone because Plaid's webhook delivery races with the page
        // reload — without the synchronous submit, UserKyc may not exist
        // by the time we reload, leaving the user stuck on the IDV step.
        // The IDENTITY_VERIFICATION_CLOSE_UI onEvent listener is a
        // belt-and-suspenders fallback if onSuccess doesn't fire.
        startIdv() {
            if (!this.idv_link_token) { return; }
            this.verifying = true;
            const handler = window.Plaid.create({
                token: this.idv_link_token,
                onSuccess: (publicToken, metadata) => {
                    this.finishIdv(publicToken, metadata);
                },
                onExit: () => {
                    this.verifying = false;
                },
                onEvent: (eventName) => {
                    if (eventName === 'IDENTITY_VERIFICATION_CLOSE_UI') {
                        router.reload({ preserveScroll: false });
                    }
                },
            });
            handler.open();
        },
        async finishIdv(publicToken, metadata) {
            try {
                await axios.post(this.route('plaid.idv.submit'), {
                    public_token: publicToken,
                    metadata,
                });
            } catch (e) {
                // Best-effort: even if the server-side submit hiccups,
                // Plaid's webhook will create the UserKyc row eventually.
                // Reload anyway so the user isn't stuck on the IDV step.
            } finally {
                this.verifying = false;
                router.reload({ preserveScroll: false });
            }
        },

        // Real Plaid Link integration. Mirrors the pattern in
        // Pages/BankAccounts/TenantBankAccounts.vue: fetch a public_token from
        // our backend, open Plaid.create(), exchange the resulting public_token
        // server-side via plaid.exchange-token-and-retrieve-account (which
        // creates a UserBankAccount row), then router.reload() to surface the
        // new account in the dropdown.
        async connectBank() {
            this.connectingBank = true;
            this.errorMessage = '';
            try {
                const tokenRes = await axios.post(this.route('plaid.create-link-token'), { product: 'auth' });
                if (!tokenRes.data?.status || !tokenRes.data?.public_token) {
                    this.errorMessage = 'We couldn\'t open the bank connector. Please try again.';
                    this.connectingBank = false;
                    return;
                }
                this.openPlaidLink(tokenRes.data.public_token);
            } catch (e) {
                this.errorMessage = 'We couldn\'t open the bank connector. Please try again.';
                this.connectingBank = false;
            }
        },
        openPlaidLink(linkToken) {
            const handler = window.Plaid.create({
                token: linkToken,
                onSuccess: (publicToken, metadata) => {
                    this.finishAuth(publicToken, metadata);
                },
                onExit: () => {
                    this.connectingBank = false;
                },
            });
            handler.open();
        },
        async finishAuth(publicToken, metadata) {
            try {
                await axios.post(this.route('plaid.exchange-token-and-retrieve-account'), {
                    public_token: publicToken,
                    metadata,
                });
                router.reload({ preserveScroll: true });
            } catch (e) {
                this.errorMessage = 'Connecting your bank account failed. Please try again or contact support.';
            } finally {
                this.connectingBank = false;
            }
        },

        submit() {
            const sig = this.$refs.signaturePad;
            if (sig.isEmpty()) {
                this.form.errors.authorization_signature_base64 = 'Please sign to authorize the autopay.';
                return;
            }
            if (!this.form.user_bank_account_id) {
                this.form.errors.user_bank_account_id = 'Please connect or select a bank account first.';
                return;
            }
            this.form.authorization_signature_base64 = sig.save();
            this.form.amount_cents = Math.round(parseFloat(this.amountDollars) * 100);
            this.form.post(this.route('autopay.store', this.rentedProperty.id));
        },
        confirmCancel() {
            this.showCancelModal = true;
        },
        confirmCancelInModal() {
            this.cancelling = true;
            router.delete(this.route('autopay.destroy', this.schedule.id), {
                onFinish: () => {
                    this.cancelling = false;
                    this.showCancelModal = false;
                },
            });
        },
    },
};
</script>
